import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';

import { Button } from './button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './dialog';
import { Label } from './label';
import { formatLira, MoneyInput } from './money-input';

/**
 * Replacement for native prompt() on money entry (redesign-admin.md §0 D1):
 * kuruş-aware input with min validation, Enter submits, Esc cancels (via the
 * dialog). Labels/errors are passed in — i18n lives in the apps.
 */
export function AmountDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  min,
  minLabel,
  initial = null,
  confirmLabel,
  cancelLabel,
  busy,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  label: string;
  /** Minimum accepted amount in kuruş (exclusive of null); omit for any positive amount. */
  min?: number;
  /** Validation message shown when the entered amount is below `min` or empty. */
  minLabel: string;
  initial?: number | null;
  confirmLabel: string;
  cancelLabel: string;
  busy?: boolean;
  onSubmit: (kurus: number) => void;
}) {
  const id = useId();
  const [value, setValue] = useState<number | null>(initial);
  const [touched, setTouched] = useState(false);

  // Re-seed when (re)opened — a stale amount from the previous use is a misfire risk.
  useEffect(() => {
    if (open) {
      setValue(initial);
      setTouched(false);
    }
  }, [open, initial]);

  const invalid = value == null || (min != null && value < min);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (invalid || busy) return;
    onSubmit(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {description && <div className="text-sm text-muted-foreground">{description}</div>}
          <div className="space-y-1.5">
            <Label htmlFor={id}>{label}</Label>
            <MoneyInput
              id={id}
              autoFocus
              value={value}
              onValueChange={setValue}
              aria-invalid={touched && invalid}
              aria-describedby={touched && invalid ? `${id}-error` : undefined}
            />
            {min != null && (
              <div className="text-xs text-muted-foreground">≥ ₺{formatLira(min)}</div>
            )}
            {touched && invalid && (
              <div id={`${id}-error`} role="alert" className="text-xs text-destructive">
                {minLabel}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>
              {cancelLabel}
            </Button>
            <Button type="submit" disabled={busy}>
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
